<script setup lang="ts">
import { mdiInformationOutline } from '@mdi/js'

interface Props {
  sessionUrl: string
  /**
   * #663: Pyodide built-in package names to preload (e.g. ["numpy"]).
   * From the session creation response.
   */
  pyodidePackages?: string[]
  /**
   * #764: ビルドされた pyxapp のサイズ。押した後に落ちて初めて分かる、を
   * 押す前に分かるへ変える。プレビューのビルドはもう終わっているので追加コストなし。
   */
  sizeBytes?: number | null
}

const props = defineProps<Props>()
const emit = defineEmits<{
  // #707: each clip carries the paired OGP still (PNG data URL) captured at the
  // same tick as the GIF clip. `stillDataUrl` may be undefined if the still for
  // that tick didn't arrive (rare); the backend then falls back to cover-only.
  done: [payload: { clips: Array<{ dataUrl: string, timestamp: number, stillDataUrl?: string }> }]
  cancel: []
}>()

const modelValue = defineModel<boolean>()

// #374: capture mode/interval/scale are switchable via URL query parameters for
// Phase B empirical comparison.
//   ?cap=keypress       → synthesizeKeypress (Alt+3 dispatched to iframe)
//   ?cap=runPython      → runPython (default)
//   ?interval=<ms>      → custom auto-capture interval (default 5000ms)
//   ?scale=<N>          → scale arg passed to pyxel.screencast() (default 1).
//                         Pyxel's own default is capture_scale=2 (upscaled).
//                         Native scale=1 yields ~4x smaller / faster GIFs.
const route = useRoute()
const triggerMode = computed<'runPython' | 'synthesizeKeypress'>(() => {
  return route.query.cap === 'keypress' ? 'synthesizeKeypress' : 'runPython'
})
const captureIntervalMs = computed<number>(() => {
  const v = route.query.interval
  if (typeof v === 'string') {
    const n = Number.parseInt(v, 10)
    if (!Number.isNaN(n) && n >= 1000) return n
  }
  return 5000
})
const captureScale = computed<number>(() => {
  const v = route.query.scale
  if (typeof v === 'string') {
    const n = Number.parseInt(v, 10)
    if (!Number.isNaN(n) && n >= 1) return n
  }
  return 1
})

const maxClips = 8

// #749: PyxelPlayer(capture) が cross-origin フレームで走るときは、親から
// contentWindow を直駆動できない。sandbox origin が設定されていれば PyxelPlayer は
// capture も cross-origin 化する（useCrossOrigin = sandboxOrigin && !trusted）ので、
// その origin を frameOrigin として渡し、useNativeScreencast を postMessage 駆動に切り替える。
// 空（ローカルで未設定）のときは undefined = 従来の同一オリジン blob 直駆動。
const runtimeConfig = useRuntimeConfig()
const frameOrigin = computed(() => runtimeConfig.public.pyxelSandboxOrigin || undefined)

const {
  screencasts,
  screenshots,
  isCapturing,
  pyxelFailed,
  startCapture,
  stopCapture,
} = useNativeScreencast({ maxClips })

/** #707 と同じ倍率。静止画（PNG）はこの倍率で撮られている。 */
const STILL_SCALE = 2

interface DisplayClip {
  /** '' のあいだは静止画からの変換待ち（#959）。 */
  dataUrl: string
  timestamp: number
  /**
   * #959: 作品が録画を切っていて（`capture_sec=0` など）GIF が無かった枠。
   * 同じ tick の静止画（stills[i]）を 1 フレームの GIF にして代用する。
   */
  fromStill?: boolean
}

const clips = ref<DisplayClip[]>([])
const seenBlobs = new WeakSet<Blob>()
const seenEmpty = new WeakSet<object>()
/**
 * #959: 撮影 1 回ごとに「次に届く静止画で埋める枠」を順に積む（録画があった回は null）。
 * 1 回の撮影では screencast → screenshot の順に必ず届くので、静止画は先頭を取れば自分の回の枠に当たる。
 * 配列の位置（clips[i] ↔ stills[i]）で引くと、静止画が 1 枚欠けた・trim された時点で以後ずっとずれる。
 */
const stillTargets: Array<DisplayClip | null> = []

// #707: OGP still PNG data URLs, index-aligned with `clips`. Each screencast
// tick fires a paired screenshot, so stills[i] corresponds to clips[i].
const stills = ref<string[]>([])
/** #959: 静止画 → GIF の変換中の数（その間は「確認完了」を押させない）。 */
const convertingStills = ref(0)
const seenStillBlobs = new WeakSet<Blob>()

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

// Eagerly convert Blob → dataUrl so onDone can emit synchronously
// (LargeDialog's finally block closes the dialog via v-if before any await resolves).
watch(screencasts, async (newList) => {
  for (const sc of newList) {
    if (sc.empty) {
      // #959: 録画が空。枠だけ先に確保し、ペアの静止画が届いたら埋める（下の watcher）。
      // 同期で push するので、#707 の clips[i] ↔ stills[i] の対応は崩れない。
      if (seenEmpty.has(sc)) continue
      seenEmpty.add(sc)
      // reactive にしておくと、trim で配列から外れても同じ枠を指したまま埋められる。
      const placeholder = reactive<DisplayClip>({ dataUrl: '', timestamp: sc.receivedAt, fromStill: true })
      stillTargets.push(placeholder)
      clips.value.push(placeholder)
      if (clips.value.length > maxClips) {
        clips.value = clips.value.slice(-maxClips)
      }
      continue
    }
    if (!sc.blob || seenBlobs.has(sc.blob)) continue
    seenBlobs.add(sc.blob)
    // 同期で積む（この回の静止画は録画の代用には使わない）。
    stillTargets.push(null)
    try {
      const dataUrl = await blobToDataUrl(sc.blob)
      clips.value.push({ dataUrl, timestamp: sc.receivedAt })
      if (clips.value.length > maxClips) {
        clips.value = clips.value.slice(-maxClips)
      }
    }
    catch (e) {
      console.error('[CrystallizePreviewDialog] dataUrl conversion failed:', e)
    }
  }
}, { deep: true })

// #707: mirror the screencast watcher for the paired OGP still PNGs. Kept
// index-aligned with `clips` (same maxClips trim) so the still for the selected
// thumbnail is stills[selectedIndex].
watch(screenshots, async (newList) => {
  for (const ss of newList) {
    if (!ss.blob || seenStillBlobs.has(ss.blob)) continue
    seenStillBlobs.add(ss.blob)
    // #959: この静止画が埋めるべき枠（同期で取る。await を挟むと次の回と入れ替わる）。
    const target = stillTargets.shift() ?? null
    try {
      const dataUrl = await blobToDataUrl(ss.blob)
      stills.value.push(dataUrl)
      if (stills.value.length > maxClips) {
        stills.value = stills.value.slice(-maxClips)
      }
      // #959: 録画が空だった回なら、この静止画を表紙用の GIF にして埋める。
      if (target && !target.dataUrl) {
        convertingStills.value++
        try {
          target.dataUrl = await stillToGifDataUrl(dataUrl, STILL_SCALE)
        }
        catch (e) {
          console.error('[CrystallizePreviewDialog] still → GIF conversion failed:', e)
        }
        finally {
          convertingStills.value--
        }
      }
    }
    catch (e) {
      console.error('[CrystallizePreviewDialog] still dataUrl conversion failed:', e)
    }
  }
}, { deep: true })

// #764: プレビュー用のビルドで確定したサイズ。無い場合（旧サーバ）は行ごと出さない。
const builtSizeLabel = computed(() => sizeLabel(props.sizeBytes))

const playerRef = ref<{ iframeEl: HTMLIFrameElement | null } | null>(null)

const appRootUrl = ref('')
const appEntryPoint = ref('')

let pollTimer: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  if (props.sessionUrl) {
    const url = new URL(props.sessionUrl)
    const pathTokens = url.pathname.split('/')
    const filename = pathTokens.pop()!
    appEntryPoint.value = filename
    appRootUrl.value = url.origin + pathTokens.join('/')
  }

  console.log(
    '[CrystallizePreviewDialog] capture mode:',
    triggerMode.value,
    'interval:',
    captureIntervalMs.value + 'ms',
    'scale:',
    captureScale.value,
  )

  // Poll for iframe availability since we can't reliably detect load on blob URLs
  pollTimer = setInterval(() => {
    if (playerRef.value?.iframeEl && !isCapturing.value) {
      startCapture(playerRef.value.iframeEl, {
        intervalMs: captureIntervalMs.value,
        triggerMode: triggerMode.value,
        scale: captureScale.value,
        // #707: also capture a paired OGP still (PNG, scale=2) each tick.
        captureStill: true,
        // #749: cross-origin フレーム時は postMessage 駆動に切り替える。
        frameOrigin: frameOrigin.value,
      })
      if (pollTimer) {
        clearInterval(pollTimer)
        pollTimer = null
      }
    }
  }, 500)
})

onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer)
})

const hasCaptures = computed(() => clips.value.some(c => c.dataUrl))
// "encoding" surrogate: there's a window where a blob has arrived but the
// dataUrl conversion is still in flight. Used to disable the submit button.
const isEncodingGif = computed(() =>
  clips.value.length < screencasts.value.length || convertingStills.value > 0,
)
// #959: 表紙が静止画になることを、枠のそばで一言だけ伝える。
const usesStillCover = computed(() => clips.value.some(c => c.fromStill))

// IMPORTANT: onDone must be synchronous to avoid race condition with LargeDialog's
// finally block closing the dialog (v-if destroys the component before async emit).
const onDone = () => {
  // #707: pair each GIF clip with its OGP still (same tick → same index).
  // #959: 静止画が届かず埋まらなかった枠は渡さない（index は map の時点で確定させる）。
  const currentClips = clips.value
    .map((c, i) => ({
      dataUrl: c.dataUrl,
      timestamp: c.timestamp,
      stillDataUrl: stills.value[i],
    }))
    .filter(c => c.dataUrl)
  if (currentClips.length === 0) return

  emit('done', { clips: currentClips })
  modelValue.value = false

  // Clean up timers (fire-and-forget, no await)
  stopCapture()
}

const onCancel = () => {
  stopCapture()
  emit('cancel')
  modelValue.value = false
}
</script>

<template>
  <DialogLargeDialog
    v-model="modelValue"
    title="動作確認とサムネイル作成"
    :cancel-button="{ title: 'キャンセル', sound: 'cancel' }"
    :submit-button="{ title: '確認完了・サムネイルを選ぶ', disabled: isEncodingGif || !hasCaptures }"
    @cancel="onCancel"
    @submit="onDone"
  >
    <div class="preview-content">
      <div class="preview-player-area">
        <PyxelPlayer
          ref="playerRef"
          mode="play"
          :root="appRootUrl"
          :entrypoint="appEntryPoint"
          :packages="props.pyodidePackages?.length ? props.pyodidePackages.join(',') : undefined"
          capture
        />
      </div>

      <div class="preview-bottom">
        <!-- #764: 「この作品の大きさ」。内訳は出さない（合計 1 つで足りる）。 -->
        <p
          v-if="builtSizeLabel"
          class="preview-size"
        >
          この作品の大きさ： <strong>{{ builtSizeLabel }}</strong>
        </p>
        <!-- #959: 作品が録画を切っている（capture_sec=0）ので、表紙は静止画になる。 -->
        <p
          v-if="usesStillCover"
          class="preview-note"
          data-testid="preview-still-cover-note"
        >
          この作品は動画を記録しない設定なので、表紙は静止画になります
          <v-tooltip
            location="top"
            max-width="320"
          >
            <template #activator="{ props: tooltipProps }">
              <v-icon
                v-bind="tooltipProps"
                size="small"
                class="preview-note-icon"
                aria-label="静止画の表紙について"
              >
                {{ mdiInformationOutline }}
              </v-icon>
            </template>
            <span>
              pyxel.init() の capture_sec が 0 だと、Pyxel は動画を記録しません。動く表紙にしたいときは、次の版で capture_sec を 2〜3 秒くらいにしてください。秒数を長くするほど、ブラウザのメモリを使います。
            </span>
          </v-tooltip>
        </p>
        <p
          v-if="pyxelFailed"
          class="preview-note"
          data-testid="preview-pyxel-failed-note"
        >
          ゲームが止まったので、ここで撮影を終えました
        </p>
        <div class="preview-strip">
          <template
            v-for="index in maxClips"
            :key="index"
          >
            <div class="preview-strip-slot">
              <img
                v-if="clips[index - 1]?.dataUrl"
                :src="clips[index - 1]!.dataUrl"
                :alt="`キャプチャ #${index}`"
                class="preview-strip-img"
              >
              <div
                v-else
                class="preview-strip-placeholder"
              >
                {{ index }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </DialogLargeDialog>
</template>

<style scoped>
.preview-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.preview-player-area {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: black;
}

.preview-player-area :deep(.pixel-player) {
  width: 100%;
  height: 100%;
}

.preview-player-area :deep(.pixel-player-inner) {
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
}

.preview-bottom {
  flex: 0 0 auto;
}

.preview-size {
  margin: 0;
  padding: max(3px, calc(0.4 * var(--u))) max(6px, calc(0.8 * var(--u)));
  background: #111;
  color: #ddd;
  font-size: max(11px, calc(1.2 * var(--u)));
  text-align: center;
}

.preview-note {
  margin: 0;
  padding: max(3px, calc(0.4 * var(--u))) max(6px, calc(0.8 * var(--u)));
  background: #111;
  color: #bbb;
  font-size: max(11px, calc(1.1 * var(--u)));
  text-align: center;
}

.preview-note-icon {
  margin-left: 4px;
  color: #8a97a5;
  vertical-align: middle;
}

.preview-strip {
  display: flex;
  gap: max(3px, calc(0.4 * var(--u)));
  padding: max(4px, calc(0.5 * var(--u)));
  justify-content: center;
  flex-wrap: wrap;
  background: #111;
}

.preview-strip-slot {
  flex: 0 0 auto;
  width: max(80px, calc(10 * var(--u)));
  aspect-ratio: 4 / 3;
  border-radius: 2px;
  overflow: hidden;
}

.preview-strip-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  image-rendering: pixelated;
  display: block;
}

.preview-strip-placeholder {
  width: 100%;
  height: 100%;
  background: #222;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #444;
  font-size: max(10px, calc(1.2 * var(--u)));
}
</style>
